// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-22 23:17:11 +0200 (Mon, 22 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156448 $
//  Purpose   : editable label with auto input width that combines
//    https://github.com/markbiek/react-editable-label
//    https://github.com/JedWatson/react-input-autosize/blob/master/src/AutosizeInput.js
// =============================================================================

// ---Third-party imports
import React, {Component} from 'react'
import render from 'react-dom'
import PropTypes from 'prop-types'
import {useResettableState, useState, useEffect, useRef} from 'uix/hooks'

// ---Framework imports
import log from 'uix/logging'
import {isBoolean} from 'ldash'

// ---Local imports
import {KeyboardShortCut} from './keyboardShortcuts'

// ---Globals
const sizerStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  visibility: 'hidden',
  height: 0,
  overflow: 'scroll',
  whiteSpace: 'pre',
}

const INPUT_PROPS_BLACKLIST = [
  'extraWidth',
  'inputClassName',
  'inputRef',
  'inputStyle',
  'minWidth',
  'onAutosize',
  'placeholderIsMinWidth',
  'onInputMouseEnter',
]

const cleanInputProps = inputProps => {
  INPUT_PROPS_BLACKLIST.forEach(field => delete inputProps[field])
  return inputProps
}

const copyStyles = (styles, node) => {
  node.style.fontSize = styles.fontSize
  node.style.fontFamily = styles.fontFamily
  node.style.fontWeight = styles.fontWeight
  node.style.fontStyle = styles.fontStyle
  node.style.letterSpacing = styles.letterSpacing
  node.style.textTransform = styles.textTransform
}

class AutosizeInput extends Component {
  constructor(props) {
    super(props)
    this.state = {
      inputWidth: props.minWidth,
      inputId: props.id,
    }
  }
  componentDidMount() {
    this.mounted = true
    this.copyInputStyles()
    this.updateInputWidth()
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {id} = nextProps
    if (id !== this.props.id) {
      this.setState({inputId: id})
    }
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevState.inputWidth !== this.state.inputWidth) {
      if (typeof this.props.onAutosize === 'function') {
        this.props.onAutosize(this.state.inputWidth)
      }
    }
    this.updateInputWidth()
  }
  componentWillUnmount() {
    this.mounted = false
  }
  inputRef = el => {
    this.input = el
    if (typeof this.props.inputRef === 'function') {
      this.props.inputRef(el)
    }
  }
  placeHolderSizerRef = el => {
    this.placeHolderSizer = el
  }
  sizerRef = el => {
    this.sizer = el
  }
  copyInputStyles() {
    if (!this.mounted || !window.getComputedStyle) {
      return
    }
    const inputStyles = this.input && window.getComputedStyle(this.input)
    if (!inputStyles) {
      return
    }
    copyStyles(inputStyles, this.sizer)
    if (this.placeHolderSizer) {
      copyStyles(inputStyles, this.placeHolderSizer)
    }
  }
  updateInputWidth() {
    if (!this.mounted || !this.sizer || typeof this.sizer.scrollWidth === 'undefined') {
      return
    }
    let newInputWidth
    if (
      this.props.placeholder &&
      (!this.props.value || (this.props.value && this.props.placeholderIsMinWidth))
    ) {
      newInputWidth = Math.max(this.sizer.scrollWidth, this.placeHolderSizer.scrollWidth) + 2
    } else {
      newInputWidth = this.sizer.scrollWidth + 2
    }
    // add extraWidth to the detected width. for number types, this defaults to 16 to allow for the stepper UI
    const extraWidth =
      this.props.type === 'number' && this.props.extraWidth === undefined
        ? 16
        : parseInt(this.props.extraWidth) || 0
    newInputWidth += extraWidth
    if (newInputWidth < this.props.minWidth) {
      newInputWidth = this.props.minWidth
    }
    if (newInputWidth !== this.state.inputWidth) {
      this.setState({
        inputWidth: newInputWidth,
      })
    }
  }
  getInput() {
    return this.input
  }
  focus(selectText) {
    this.input.focus()
    // this will move the cursor to the right in a RTL situation
    this.input.setSelectionRange(0, 0)

    if (selectText) this.input.select()
  }
  blur() {
    this.input.blur()
  }
  select() {
    this.input.select()
  }
  render() {
    const sizerValue = [this.props.defaultValue, this.props.value, ''].reduce(
      (previousValue, currentValue) => {
        if (previousValue !== null && previousValue !== undefined) {
          return previousValue
        }
        return currentValue
      }
    )

    const wrapperStyle = {...this.props.style}
    if (!wrapperStyle.display) wrapperStyle.display = 'inline-block'

    const inputStyle = {
      boxSizing: 'content-box',
      width: `${this.state.inputWidth}px`,
      ...this.props.inputStyle,
    }

    const {...inputProps} = this.props
    cleanInputProps(inputProps)
    inputProps.className = this.props.inputClassName
    inputProps.id = this.state.inputId
    inputProps.style = inputStyle

    return (
      <div className={this.props.className} style={wrapperStyle}>
        <input {...inputProps} ref={this.inputRef} onMouseEnter={this.props.onInputMouseEnter} />
        <div ref={this.sizerRef} style={sizerStyle}>
          {sizerValue}
        </div>
        {this.props.placeholder ? (
          <div ref={this.placeHolderSizerRef} style={sizerStyle}>
            {this.props.placeholder}
          </div>
        ) : null}
      </div>
    )
  }
}

AutosizeInput.propTypes = {
  className: PropTypes.string, // className for the outer element
  defaultValue: PropTypes.any, // default field value
  extraWidth: PropTypes.oneOfType([
    // additional width for input element
    PropTypes.number,
    PropTypes.string,
  ]),
  id: PropTypes.string, // id to use for the input, can be set for consistent snapshots
  inputClassName: PropTypes.string, // className for the input element
  inputRef: PropTypes.func, // ref callback for the input element
  inputStyle: PropTypes.object, // css styles for the input element
  minWidth: PropTypes.oneOfType([
    // minimum width for input element
    PropTypes.number,
    PropTypes.string,
  ]),
  onAutosize: PropTypes.func, // onAutosize handler: function(newWidth) {}
  onChange: PropTypes.func, // onChange handler: function(event) {}
  placeholder: PropTypes.string, // placeholder text
  placeholderIsMinWidth: PropTypes.bool, // don't collapse size to less than the placeholder
  style: PropTypes.object, // css styles for the outer element
  value: PropTypes.any, // field value
}

AutosizeInput.defaultProps = {
  minWidth: 1,
}

const EditableLabel = ({
  text, // text content
  emptyLabel, // placeholder if empty
  disableKeys, // disable Escape + Enter handling
  inputClass, // customize input css
  labelClass,
  id, // for label
  viewMode, // control initial value 'label' and 'text' externally
  doubleClick = false, // default is onClick, change to doubleClick here
  keyboardShortcut, // set to F2
  selectText = false, // select all text on focus
  onTabKey, // callback receives shiftState and current input text value
  onKeyDown,
  onSave, // called with (text) on Enter key-up
  onCancel, // called on Escape key-up
  onMouseEnter,
  onInputMouseEnter,
  onStartEdit,
  onEndEdit,
  style,
  title,
  setLastCursorState,
  readonly = false,
}) => {
  let [view, setView] = useState(null)
  if (!view) {
    view = viewMode || 'label'
  }
  useEffect(() => {
    setView(null)
  }, [viewMode])

  let [value, setValue] = useResettableState(text, [text])
  if (readonly) {
    value = text
    setValue = () => null
  }
  const [previous, setPrevious] = useResettableState(text, [text])
  const textInput = useRef(null)

  useEffect(() => {
    if (view === 'text') {
      textInput.current.focus(selectText)
    }
  }, [view, textInput])

  const closeEditor = () => {
    setView('label')
    if (onEndEdit) onEndEdit()
  }

  const keyUp = e => {
    if (disableKeys) {
      return
    }
    if (e.key === 'Escape') {
      setValue(previous)
      closeEditor()
      if (onCancel) {
        onCancel()
      }
    } else if (e.key === 'Enter') {
      let new_val = e.target.value
      if (onSave && text != e.target.value) {
        let res = onSave(e.target.value)
        if (isBoolean(res) && !res) {
          new_val = previous
        }
      }

      setValue(new_val)
      setPrevious(new_val)
      closeEditor()
    }
  }
  const keyDown = e => {
    if (e.key === 'Tab' && onTabKey) {
      // also send current input text value
      onTabKey(!!e.shiftKey, e.target.value)
      e.preventDefault()
    }
    if (onKeyDown) {
      onKeyDown(e)
    }
  }
  const switchToEditmode = () => {
    setView('text')
    if (onStartEdit) onStartEdit()
  }

  const renderLabel = () => {
    let handler = doubleClick ? 'onDoubleClick' : 'onClick'
    let props = {[handler]: switchToEditmode}

    return (
      <span
        className={labelClass}
        id={id}
        onMouseEnter={onMouseEnter}
        style={style}
        title={title}
        {...props}
      >
        {keyboardShortcut && (
          <KeyboardShortCut name={keyboardShortcut} onTrigger={switchToEditmode} />
        )}
        {value || emptyLabel}
      </span>
    )
  }

  const renderInput = () => (
    <AutosizeInput
      type="text"
      ref={textInput}
      value={value}
      inputClassName={inputClass}
      onChange={e => setValue(e.target.value)}
      onBlur={e => {
        let lastCursorState = null
        if (setLastCursorState) {
          const element = e.target
          lastCursorState = {
            element,
            setValue,
            selectionStart: element.selectionStart,
            selectionEnd: element.selectionEnd,
          }
        }
        const targetValue = e.target.value
        setTimeout(() => {
          // without timeout activeElement is whole body
          const nextEl = document.activeElement
          if (
            nextEl.classList.contains('specialCharsButton') ||
            !!nextEl.closest('.specialCharPopupContent') ||
            !!nextEl.closest('.specialCharsInlineBar')
          ) {
            // if clicking sth. within special char popup or inline bar -> keep focus within editableLabel
            if (lastCursorState) setLastCursorState(lastCursorState)
            textInput.current.focus()
          } else {
            closeEditor()
            setPrevious(targetValue)
            if (onSave && text != targetValue) onSave(targetValue)
          }
        }, 0)
      }}
      onKeyUp={keyUp}
      onKeyDown={keyDown}
      onInputMouseEnter={onInputMouseEnter}
    />
  )
  return view === 'label' ? renderLabel() : renderInput()
}

EditableLabel.propTypes = {
  text: PropTypes.string.isRequired,
  labelClass: PropTypes.string,
  inputClass: PropTypes.string,
  disableKeys: PropTypes.bool,
}

export {EditableLabel}
